import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Phone, ArrowRight, Check } from "lucide-react";
import { BUSINESS } from "../data/site";

export const EASE = [0.22, 1, 0.36, 1];

export const Reveal = ({ children, delay = 0, y = 28, className = "", ...rest }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.15 }}
    transition={{ duration: 0.7, delay, ease: EASE }}
    {...rest}
  >
    {children}
  </motion.div>
);

export const MaskedLines = ({ lines, as: Tag = "h1", className = "", delay = 0 }) => (
  <Tag className={className}>
    {lines.map((line, i) => (
      <span key={i} className="block overflow-hidden pb-[0.08em] -mb-[0.08em]">
        <motion.span
          className="block will-change-transform"
          initial={{ y: "112%" }}
          animate={{ y: 0 }}
          transition={{ duration: 0.95, delay: delay + i * 0.13, ease: EASE }}
        >
          {line}
        </motion.span>
      </span>
    ))}
  </Tag>
);

export const Eyebrow = ({ children, className = "" }) => (
  <p className={`font-mono text-[11px] sm:text-xs uppercase tracking-[0.25em] text-glow ${className}`}>
    {children}
  </p>
);

export const SectionHead = ({ eyebrow, title, lead, align = "left" }) => (
  <div className={`max-w-3xl ${align === "center" ? "mx-auto text-center" : ""}`}>
    <Reveal><Eyebrow>{eyebrow}</Eyebrow></Reveal>
    <Reveal delay={0.08}>
      <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.05]">
        {title}
      </h2>
    </Reveal>
    {lead && (
      <Reveal delay={0.16}>
        <p className="mt-5 text-base sm:text-lg text-steel leading-relaxed">{lead}</p>
      </Reveal>
    )}
  </div>
);

export const CallBookButtons = ({ center = false, testPrefix = "hero" }) => (
  <div className={`flex flex-col sm:flex-row gap-3.5 ${center ? "items-center justify-center" : ""}`}>
    <Link
      to="/book"
      data-testid={`${testPrefix}-book-button`}
      className="group relative overflow-hidden inline-flex items-center justify-center gap-2 bg-bright hover:bg-accent text-white font-semibold rounded-full px-7 py-3.5 transition-colors"
    >
      Book Your Detail
      <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
    </Link>
    <a
      href={BUSINESS.phoneHref}
      data-testid={`${testPrefix}-call-button`}
      className="inline-flex items-center justify-center gap-2 border border-white/20 hover:border-bright hover:text-bright text-white font-semibold rounded-full px-7 py-3.5 transition-colors"
    >
      <Phone size={16} /> {BUSINESS.phone}
    </a>
  </div>
);

export const PhotoFrame = ({ src, alt, className = "", imgClass = "", children }) => (
  <div className={`relative overflow-hidden rounded-2xl border border-white/10 bg-card group ${className}`}>
    <img
      src={src}
      alt={alt}
      loading="lazy"
      className={`w-full h-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-[1.04] ${imgClass}`}
    />
    <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-transparent to-transparent pointer-events-none" />
    {children}
  </div>
);

export const CheckList = ({ items, cols = 1 }) => (
  <ul className={`grid gap-3 ${cols === 2 ? "sm:grid-cols-2" : ""}`}>
    {items.map((item, i) => (
      <li key={i} className="flex items-start gap-3 text-sm text-steel leading-relaxed">
        <span className="mt-0.5 shrink-0 flex h-5 w-5 items-center justify-center rounded-full bg-bright/15 border border-bright/30">
          <Check size={11} className="text-bright" />
        </span>
        {item}
      </li>
    ))}
  </ul>
);

export const CtaBand = ({ testPrefix = "cta" }) => (
  <section className="relative overflow-hidden">
    <div className="absolute inset-0 hero-bg" />
    <div className="relative mx-auto max-w-7xl px-5 sm:px-8 py-24 sm:py-28">
      <Reveal>
        <Eyebrow>Sioux Falls · Mobile & In-Shop</Eyebrow>
        <h2 className="mt-4 font-display text-3xl sm:text-5xl font-extrabold tracking-tight leading-[1.05] max-w-2xl">
          Your vehicle deserves the A+ treatment.
        </h2>
        <p className="mt-5 text-steel text-lg max-w-xl">
          Book your detail online or request a free quote — we'll take it from there.
        </p>
        <div className="mt-8">
          <CallBookButtons testPrefix={testPrefix} />
        </div>
      </Reveal>
    </div>
  </section>
);

export const PageHero = ({ eyebrow, lines, lead, chips = [], testPrefix }) => (
  <section className="relative pt-36 pb-16 sm:pt-44 sm:pb-24 overflow-hidden">
    <div className="absolute inset-0 hero-bg" />
    <div className="absolute inset-0 hero-grid opacity-40" />
    <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
      <Eyebrow>{eyebrow}</Eyebrow>
      <MaskedLines
        lines={lines}
        delay={0.1}
        className="mt-5 font-display text-4xl sm:text-6xl lg:text-7xl font-extrabold uppercase tracking-tight leading-[0.98]"
      />
      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.5, ease: EASE }}
        className="mt-6 max-w-2xl text-base sm:text-lg text-steel leading-relaxed"
      >
        {lead}
      </motion.p>
      {chips.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.65, ease: EASE }}
          className="mt-8 flex flex-wrap gap-3"
        >
          {chips.map((c, i) => (
            <span key={i} className="glass border border-white/10 rounded-full px-4 py-2 text-sm text-white">
              {c}
            </span>
          ))}
        </motion.div>
      )}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.8, ease: EASE }}
        className="mt-9"
      >
        <CallBookButtons testPrefix={testPrefix} />
      </motion.div>
    </div>
  </section>
);
